import React, { useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import RadioButtonGroup from "../atoms/RadioGroup";
import {
  Dimension,
  DimensionType,
  Question,
  QuestionProps,
  QuestionType,
} from "../../types/types";
import apiOperation from "../../apihelper/api-operation";
import AtomicButton from "../atoms/Button";
import TableHeading from "../atoms/TableHeading";
import { createTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CircularProgressBar from "../atoms/CircularProgressBar";

const QuestionAnswer: React.FC<
  QuestionProps & { selectedDimension: Dimension[] }
> = ({
  dimensionID,
  handleNext,
  handleBack,
  selectedDimension,
  questionOptionsData,
  dimensionsData,
}) => {
  const [question, setQuestion] = useState<QuestionType[]>([]);
  const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedDimensions, setSelectedDimensions] = useState<Dimension[]>([]);  
  const [isLoading, setIsLoading] = useState(true); // New loading state

  const getQuestions = async () => {
    setIsLoading(true); // Start loading indicator
    apiOperation.url = `question/${dimensionID}`;
    const questionsData = await apiOperation.getAll();
    setQuestion(questionsData);
    setIsLoading(false); // Stop loading indicator after questions are set   
  };
  
  const getLevelNarratives = async (dimensionID: any, LevelId: any) => {
    apiOperation.url = `narrativeByDimensionIdLevelId/${dimensionID}/${LevelId}`;
    const levelNarrativeData = await apiOperation.getAll();    
    return levelNarrativeData.NarrativeText;
  };

  const getLevelByPercentage = async (percentage: number) => {
    if (
      !Number.isNaN(percentage) &&
      percentage !== undefined &&
      percentage !== null
    ) {
      apiOperation.url = `levelByPercentage/${percentage}`;
      const levelData = await apiOperation.getAll();         
      if (levelData?.ID !== undefined) 
      return levelData;
    }
  };

  useEffect(() => {
    void getQuestions();
  }, [dimensionID, dimensionsData?.length > 0]);

  useEffect(() => {
    // Create a new dimension based on selectedDimension
    const filteredDimensions = selectedDimension.filter(
      (dimension: Dimension) => dimension.ID === dimensionID
    );
    const dimension = dimensionsData.filter(
      (dimension: DimensionType) => dimension.ID === dimensionID
    );
    if (dimension !== undefined) {
      const newDimension = {
        ID: dimensionID,
        Name: dimension[0]?.Name, // filteredDimensions[0]?.Name,
        MaxScore: dimension[0]?.MaxScore, //filteredDimensions[0]?.MaxScore,
        Percentage: filteredDimensions[0]?.Percentage || 0,
        Level: filteredDimensions[0]?.Level || "",
        LevelId: filteredDimensions[0]?.LevelId || 0,
        Narrative: filteredDimensions[0]?.Narrative || "",
        questions: question?.map((q) => {
          const existingAnswer = filteredDimensions[0]?.questions.find(
            (answer: Question) => answer.ID === q.ID
          );

          if (existingAnswer) {
            return {
              ...existingAnswer,
            };
          }

          return {
            ID: q.ID,
            QuestionText: q.QuestionText,
            Weight: q.Weight,
            Max: q.Max,
            Answer: {
              ID: 0,
              QuestionAnswerText: "",
              Score: "",
            },
          };
        }),
      };

      setSelectedDimensions([newDimension]);
    }
  }, [question, dimensionID]);

  const handleRadioChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    questionID: number,
    dimensionID: number
  ) => {
    const newSelectedAnswers: Dimension[] = selectedDimensions;
    const selectedOption = questionOptionsData.find(
      (option) => option.ResponseText === event.target.value
    );

    if (selectedOption) {
      const updatedSelectedAnswers = newSelectedAnswers.map((answer) => {
        if (answer.ID === dimensionID) {
          return {
            ...answer,
            questions: answer.questions.map((question) => {
              if (question.ID === questionID) {
                return {
                  ...question,
                  Answer: {
                    ...question.Answer,
                    ID: selectedOption.ID,
                    QuestionAnswerText: event.target.value,
                    Score: selectedOption.Score,
                  },
                };
              }
              return question;
            }),
          };
        }
        return answer;
      });

      // Now, update the state with the updatedSelectedAnswers array
      setSelectedDimensions(updatedSelectedAnswers);    
    }
  };

  useEffect(() => {
    setNextButtonEnabled(areAllOptionsSelected());
  }, [selectedDimensions]);

  const calculatePercentage =  async (): Promise<Dimension[]> => {
    const percentage: number = await calculateTotalScoreForDimension(
      selectedDimensions[0]
    );
    if (
      !Number.isNaN(percentage) &&
      percentage !== null &&
      percentage !== undefined
    ) {
      const parsedPercentage = percentage;
     

      if (
        !isNaN(parsedPercentage) &&
        parsedPercentage !== null &&
        parsedPercentage !== undefined
      ) {
      const levelData=  await getLevelByPercentage(parsedPercentage);            
      const narrative =   await getLevelNarratives(dimensionID, levelData.ID); 
      // Create an updated dimension object with the new values
      const updatedDimension = {
        ...selectedDimensions[0],
        Percentage: percentage,
        Level: levelData.Name, // Update Level with the level Name
        LevelId: levelData.ID,  // Update LevelId with the level ID
        Narrative: narrative,
      };
      // Update the state with the updated dimension
      setSelectedDimensions( [
        updatedDimension,  // Replace the first dimension with the updated one
      ]);
    // Return the updated dimension array (if you need it in the calling function)
      return [updatedDimension];
      }
    }
  // Return the original selectedDimensions if no update occurred
  return selectedDimensions;
   
  };

  const areAllOptionsSelected = () => {
    // Get an array of Dimension objects from the selectedDimensions object
    const dimensionsArray = Object.values(selectedDimensions);
   // Check if all questions in all dimensions have answers
    let isvalid = false;
    isvalid = dimensionsArray.every((dimension) => {
      return dimension?.questions?.every((question) => {
        return question.Answer.QuestionAnswerText !== "";
      });
    });
    return isvalid;
  };

  // Function to calculate the total score for a question
  const calculateTotalScoreForDimension = (dimension: Dimension) => {
    const totalScoreForDimension = dimension?.questions.reduce(
      (sum, question) => {
        const weight = question.Weight;
        const score = question.Answer.Score;
        if (typeof score === "number" && isFinite(score)) {
          return sum + weight * score;
        }
        return sum;
      },
      0
    );
    const maxScoreForDimension: number = dimension?.MaxScore;   
    const percentage: string = (
      (totalScoreForDimension / maxScoreForDimension) *
      100
    ).toFixed(2);   
    const roundedPercentage: number = Math.round(parseFloat(percentage));
    console.log("Percentage", roundedPercentage);
    return roundedPercentage;
  };

  return (
    <>
      { isLoading && (  // Show loading indicator while questions are being fetched( // Render loading spinner when isLoading is true
        <CircularProgressBar />)
      }
      <>
        {!isSmallScreen && (
          <TableHeading questionOptions={questionOptionsData} />
        )}

        {question.map((question, index) => (
          <Grid
            key={index}
            container
            className="question-container"
            spacing={1}
          >
            <Grid item xs={12} md={3}>
              <Typography className="question-text" component="div">
                <div>
                  <span className="serial-number">
                    {dimensionID}.{index + 1}:
                  </span>{" "}
                  {question.QuestionText}
                </div>
              </Typography>
            </Grid>
            <Grid item xs={12} lg={9} className="questionoption-container">
              <RadioButtonGroup
              //  key={`radioGroup-${dimensionID}-${question.ID}`}
                name={`myRadioGroup-${question.ID}`}
                label=""
                value={
                  selectedDimensions[0]?.questions.find(
                    (q) => q.ID === question.ID
                  )?.Answer.QuestionAnswerText || ""
                }
                options={questionOptionsData}
                onChange={async (event: React.ChangeEvent<HTMLInputElement>) =>
                 await handleRadioChange(event, question.ID, dimensionID)
                }
                variant="inline"
                className="radio-group-list"
              />
            </Grid>
          </Grid>
        ))}

        <div className="button-container">
          <div className="btn-component-page">
            {dimensionID !== 1 && (
              <AtomicButton text="Previous" onClick=
              {async (e) => {
                e.preventDefault();              
                const backUpdatedDimension =  await calculatePercentage();
                console.log("updatedDimension on back",backUpdatedDimension);
                await handleBack(backUpdatedDimension);                
                }}
              ></AtomicButton>
            )}
          </div>

          <div className="btn-component-page">
            <AtomicButton
              text="Next"
              disabled={!nextButtonEnabled}
              onClick={async (e) => {
              e.preventDefault();
              setNextButtonEnabled(false);
              const nextUpdatedDimension =  await calculatePercentage();
              console.log("updatedDimension on next",nextUpdatedDimension);
              await handleNext(nextUpdatedDimension);                
              }}
            ></AtomicButton>
          </div>
        </div>
      </>
    </>
  );
};

export default QuestionAnswer;
